export default {
  roadmapManualMetadata: {
    titles: {
      create: 'Creating development plan',
      document: 'Individual development plan',
      tasks: 'Development plan tasks',
      decisions: 'Plan decisions',
      commonTasks: 'General tasks',
      skillTasks: 'Tasks by competency',
      employeeRequest: 'Employee request',
      returnRoadmap: 'Reasons for returning the development plan',
    },
    descriptions: {
      create:
        'Select how many employees you are creating a development plan for and fill in all the required fields in the form.',
      noSupervisor: 'No supervisor assigned — select a mentor',
      selectedTasks: 'Selected tasks: {count}',
    },
    buttons: {
      propose: 'Propose development plan',
      create: 'Create development plan',
      open: 'Open development plan',
      board: 'Go to task board',
      tasksStat: 'View task statistics',
      cancel: 'Cancel development plan',
      delete: 'Delete draft',
      start: 'Start',
      sendApproval: 'Send for approval',
      approve: 'Approve',
      returnRevision: 'Return for revision',
      sendAcceptance: 'Send for completion acceptance',
      accept: 'Accept completion',
      returnWork: 'Return to work',
      startWork: 'Start work',
      submitForApproval: 'Submit for approval',
      submitForAcceptance: 'Submit for acceptance',
      cancelPlan: 'Cancel plan',
      addTask: 'Add task',
      hideFromBoard: 'Hide from board',
      removeTask: 'Remove task',
      addMoreTask: 'Add another task',
    },
    formLabels: {
      team: 'Team / department',
      teamGroup: 'Department',
      goalType: 'Development goal type',
      reason: 'Creation reason',
      target: 'Development plan goal',
      skillType: 'Skill type',
      approver: 'Approver',
      source: 'Development plan source',
      needApproval: 'Supervisor approval required',
      singleMode: 'For one employee',
      groupMode: 'For a group of employees',
      materialType: 'Material type',
      competency: 'Competency',
      taskName: 'Task name',
    },
    detailsLabels: {
      why: 'Why the employee wants a plan',
      subject: 'What the employee wants to develop',
      result: 'Expected result',
    },
    tableHeaders: {
      decision: 'Decision',
      date: 'Date and time',
    },
    sources: {
      manual: 'Created manually',
      selfTest: 'From self-assessment',
      test360: 'From 360° assessment',
    },
    roles: {
      mentor: 'Mentor',
      approver: 'Approver',
      both: 'Mentor, Approver',
    },
    stubs: {
      noTasks: 'There are no tasks in this plan yet',
      filtered: 'No results match the selected filters',
    },
    // messages
    successMessages: {
      addToBoard: 'Tasks added to the board',
      hideFromBoard: 'Tasks hidden from the board',
      deleteTasks: 'Tasks deleted',
      action: 'Development plan status changed',
    },
    errorMessages: {
      maxEmployees: 'You can select no more than {count} employees',
      createTasks: 'Failed to add tasks',
      changeBoardTasks: 'Failed to change task visibility on the board',
      deleteSelectedTasks: 'Failed to delete selected tasks',
      save: 'Failed to save development plan',
      returnRoadmap: 'Failed to return the development plan',
      load: 'Failed to load development plan',
      options: 'Failed to load editing options',
      action: 'Failed to perform action',
      deleteTask: 'Failed to delete task',
    },
    // confirm dialogs
    confirmDeleteTask: {
      title: 'Delete task?',
      text: 'The task will be permanently deleted.',
    },
    confirmDeleteSelectedTasks: {
      title: 'Delete selected tasks?',
      text: 'The selected tasks will be permanently deleted.',
    },
    confirmSpecChange: {
      title: 'Confirm specialization change',
      automaticOnly:
        'The task list will be rebuilt for the selected specialization. Current plan tasks will be replaced.',
      withManual:
        'The task list will be rebuilt for the selected specialization. Manually added tasks will be kept but will lose their competency link.',
    },
    confirmDiscardChanges: {
      title: 'Discard changes?',
      text: 'All unsaved changes will be lost.',
      button: 'Yes, discard changes',
    },
    confirmCancelRoadmap: {
      title: 'Cancel the development plan?',
      text: 'The plan will be moved to the «Cancelled» status. There is no way to revert from the «Cancelled» status — you cannot resume the plan; you need to create a new plan.',
      button: 'Yes, cancel',
      buttonCancel: "Don't cancel",
    },
    confirmDeleteRoadmap: {
      title: 'Delete the draft development plan?',
      text: 'The draft will be deleted without the possibility of recovery.',
    },
  },
  roadmapMetadata: {
    changeMentorTitle: 'Appoint a mentor',
    createMaterialTitle: 'New material',
    changeMaterialTitle: 'Edit the material',
    formLabels: {
      name: 'Name of the material',
      skillId: 'Competence',
      deadline: 'Deadline',
      typeId: 'Type of material',
      importance: 'Importance',
    },
    statuses: {
      all: 'All',
      new: 'New',
      inProgress: 'In progress',
      done: 'Done',
    },
    addMaterialFieldsButton: 'Add a material',
    removeMaterialFieldsButton: 'Delete the material',
    moveMaterialsButton: {
      top: 'Move to other skills for development',
      general: 'Move to obvious growth areas',
    },
    headers: {
      name: 'Name',
      type: 'Type',
      importance: 'Importance',
      deadline: 'Deadline',
      status: 'Status',
    },
    tooltips: {
      manualMaterial: 'The material created in the development plan',
      manualTask: 'The task created in the development plan',
      showMaterial: 'The material added to the board',
      showTask: 'The task added to the board',
      supervisorButton: 'Why update and fix the development plan',
      employeeButton: 'Why update the development plan',
      employee:
        'Update the development plan to get the most up-to-date results based on the latest estimates. Note that the current progress will be reset, only tasks that were added to the plan manually will not be deleted.',
      supervisor:
        'Update the development plan to get the most up-to-date results based on the latest estimates. Note that the current progress will be reset, only tasks that were added to the plan manually will not be deleted. After fixing the plan, the «Update development plan» button will become unavailable, but you will be able to add, delete and move tasks between blocks.',
      button: 'Why update and fix the development plan',
      text: 'Update the development plan to get the most up-to-date results based on the latest estimates. Note that the current progress will be reset, only tasks that were added to the plan manually will not be deleted. After fixing the card, the «Update development plan» button will become unavailable. Making changes to the plan will also become impossible.',
    },
    selectDateButton: 'Select a date',
    selectImportanceButton: 'Select a importance',
    devPlanTitle: 'Competence development plan',
    recreateRoadmapButton: 'Update development plan',
    fixateRoadmapButton: 'Fixate changes',
    requiredTarget: 'The «Target Description» and «Deadline» fields are required',
    commonMaterialsTitle: 'Common materials and tasks for development',
    blocksMaterialsTitle: 'Materials and tasks based on the assessment results',
    topSectionTitle: 'Obvious growth areas',
    generalSectionTitle: 'Other materials and tasks for development',
    progressDescription: 'Overall progress of the development plan',
    lowSkills: 'Competencies for which the user has the lowest evaluations',
    // messages
    successMessages: {
      changeMentor: 'Mentor successfully changed',
      saveTarget: 'Target saved successfully',
      createMaterial: 'The material successfully created',
      changeMaterial: 'The material successfully changed',
      showMaterials: 'The materials successfully added to the board',
      moveMaterials: 'The materials successfully moved',
      deleteMaterials: 'The materials successfully deleted',
      changeTask: 'The task successfully changed',
      recreateRoadmap: 'The development plan successfully updated',
      fixateRoadmap: 'The development plan successfully fixed',
    },
    errorMessages: {
      changeMentor: 'Failed to change the mentor',
      saveTarget: 'Failed to save the target',
      createMaterial: 'Failed to create a material',
      changeMaterial: 'Failed to change the material',
      showMaterials: 'Failed to add the materials to the board',
      moveMaterials: 'Failed to move the materials',
      deleteMaterials: 'Failed to delete the materials',
      changeTask: 'Failed to change the task',
      recreateRoadmap: 'Failed to update the development plan',
      fixateRoadmap: 'Failed to fixate the development plan',
      targetRoadmap: 'You need to fill in the «Target» field to fixate your development plan',
    },
    // confirm dialogs
    confirmShowMaterials: {
      title: 'Add materials to the board?',
      button: 'Yes, add',
    },
    confirmMoveMaterials: {
      title: 'Move the materials?',
      button: 'Yes, move',
    },
    confirmDeleteMaterials: {
      title: 'Delete the materials?',
      text: 'It will be impossible to cancel this action',
    },
    confirmRecreateRoadmap: {
      title: 'Update the development plan?',
      text: 'The development plan will be recreated and the changes will be lost.',
      button: 'Yes, update',
    },
    confirmFixateRoadmap: {
      title: 'Fixate the development plan?',
      text: 'Making changes to the composition of the development plan will become unavailable.',
      button: 'Yes, fixate',
    },
    //
  },
};
